import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/cn'

/* The chrome the two quote surfaces are drawn in.
 *
 * A quote is looked at through two routes — `/quotes/new` while it is
 * being written and `/quotes/:id` once it exists — and they render the SAME
 * section components (QuoteSections, RouteOptionsCard, PricingDeskCard, …)
 * inside two different frames. That is what made them read as two products: the
 * builder put its title on a bordered surface with a 340px rail beside it, the
 * detail page put the same facts in bare text over a full-width column, and the
 * two disagreed about the page's gap (3.5 against 4) on top of that. A reader
 * moving between them had to re-find the back arrow, the number and the status
 * every time.
 *
 * So the FRAME lives here and the two routes stay two routes. Nothing in this
 * file knows what a quote is: it takes a title, some badges, a subtitle line, a
 * trailing slot and a rail, and it decides only where they sit and how far
 * apart. Navigation, the step gate and what each page loads are untouched —
 * this is the shape around them.
 *
 * Do not add a fact of its own to any of these. The moment this file reads a
 * record it becomes a third opinion about the quote, competing with the two
 * pages it exists to make look alike.
 */

/** The page itself: one column of blocks, at the gap both surfaces now use.
 *
 * `min-w-0` is load-bearing, and is the reason this is a component rather than
 * a class string. A flex child defaults to its CONTENT width, so a wide costing
 * table inside would scroll the whole page sideways instead of scrolling inside
 * its own card — a property that has to hold on both routes and did, at one
 * point, hold on only one. */
export function QuotePage({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-col gap-3.5">{children}</div>
}

export interface QuotePageHeaderProps {
  /** Where the back arrow goes. Both surfaces answer `/quotes`. */
  backTo: string
  backLabel: string
  /** The h1. `asa-num` is applied unconditionally — the detail page's title IS
   *  the quote number, and tabular figures cost a title without digits
   *  nothing. */
  title: string
  /** Chips and pills that belong ON the title line: the number, a status, a
   *  readiness badge. Beside the heading rather than under it, because they
   *  qualify the record's name rather than describing it. */
  badges?: ReactNode
  /** The one line under the title — who the quote is for, and where it goes. */
  subtitle?: ReactNode
  /** The right end of the strip: the builder's step rail, the detail page's
   *  actions. Pushed there by `ml-auto` rather than by a growing spacer,
   *  because this row WRAPS and an empty flex-1 box lands alone on a line of
   *  its own once it does. */
  trailing?: ReactNode
}

/** The page header, as one strip on a card surface.
 *
 * One strip rather than a title above a rail above a banner: the three used to
 * take a third of the screen between them before the first field. The surface
 * is what tells the reader this is the page's own furniture and not the first
 * of its cards. */
export function QuotePageHeader({
  backTo,
  backLabel,
  title,
  badges,
  subtitle,
  trailing,
}: QuotePageHeaderProps) {
  return (
    <header className="bg-surface border-border-subtle flex min-w-0 flex-wrap items-center gap-x-5 gap-y-3 rounded-[var(--radius-lg)] border px-4 py-3 shadow-xs">
      {/* The way out, as a target rather than as a word. Bordered because a
          bare glyph beside a heading reads as decoration; it has to look
          pressable. */}
      <Link
        to={backTo}
        aria-label={backLabel}
        className="border-border-default text-text-muted hover:border-border-strong hover:text-text-heading flex size-[34px] shrink-0 items-center justify-center rounded-md border transition-colors hover:no-underline"
      >
        <ArrowLeft size={17} aria-hidden />
      </Link>

      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <h1 className="asa-num text-text-heading text-[19px] leading-none font-normal tracking-[-0.4px]">
            {title}
          </h1>
          {badges}
        </div>

        {subtitle ? (
          <div className="text-text-muted flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] font-light">
            {subtitle}
          </div>
        ) : null}
      </div>

      {trailing ? (
        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
          {trailing}
        </div>
      ) : null}
    </header>
  )
}

/** The body: the work in one column, the context in a sticky rail beside it.
 *
 * 1fr + 340px, and the breakpoint is `xl` rather than `lg` because these pages
 * sit inside the shell's own sidebar — a 340px rail at lg would squeeze the
 * costing table below the width its five columns need. Under xl the rail stacks
 * below the content, which costs the stickiness and nothing else: every fact on
 * the rail is also on a card in the column.
 *
 * `aside` is optional so a surface with no context to show still lays out as
 * one column rather than as a grid with a hole in it. */
export function QuotePageColumns({
  aside,
  children,
}: {
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'grid min-w-0 items-start gap-3.5',
        aside && 'xl:grid-cols-[minmax(0,1fr)_340px]',
      )}
    >
      <div className="flex min-w-0 flex-col gap-3.5">{children}</div>

      {aside ? (
        /* `top-4` matches the shell's own scroll padding, so the cards stop
           under the topbar rather than sliding beneath it. */
        <aside className="flex min-w-0 flex-col gap-3.5 xl:sticky xl:top-4">
          {aside}
        </aside>
      ) : null}
    </div>
  )
}
